// Identity Recovery Guide Data Structure
// Based on "The Strategist's Guide to Identity Recovery"

export const PHASES = {
  FOUNDATION: 'foundation',
  HIDDEN_BUREAUS: 'hidden_bureaus',
  STATE_RIGHTS: 'state_rights',
};

export const AGENCY_CATEGORIES = {
  CREDIT: 'credit',
  EMPLOYMENT: 'employment',
  BANKING: 'banking',
  RENTAL: 'rental',
  UTILITIES: 'utilities',
  RETAIL: 'retail',
  INSURANCE: 'insurance',
  PAYDAY: 'payday',
  FEDERAL: 'federal',
  LAW_ENFORCEMENT: 'law_enforcement',
  DMV: 'dmv',
};

export const QUESTIONNAIRE = [
  {
    id: 'situation',
    question: 'What situation are you experiencing?',
    type: 'multi-select',
    options: [
      { value: 'bank_fraud', label: 'Unauthorized bank withdrawals or charges', categories: [AGENCY_CATEGORIES.CREDIT, AGENCY_CATEGORIES.BANKING] },
      { value: 'tax_fraud', label: 'Fraudulent tax returns or IRS/SSA notices', categories: [AGENCY_CATEGORIES.FEDERAL] },
      { value: 'job_denied', label: 'Denied a job or background check failed', categories: [AGENCY_CATEGORIES.EMPLOYMENT] },
      { value: 'bank_denied', label: 'Unable to open a bank account', categories: [AGENCY_CATEGORIES.BANKING] },
      { value: 'eviction', label: 'Eviction notice for property you never rented', categories: [AGENCY_CATEGORIES.RENTAL] },
      { value: 'utility_bills', label: "Utility or phone bills you didn't authorize", categories: [AGENCY_CATEGORIES.UTILITIES] },
      { value: 'return_banned', label: 'Banned from returning items at stores', categories: [AGENCY_CATEGORIES.RETAIL] },
      { value: 'insurance_claims', label: "Insurance claims or medical bills you didn't authorize", categories: [AGENCY_CATEGORIES.INSURANCE] },
      { value: 'payday_loans', label: 'Payday or sub-prime loans in your name', categories: [AGENCY_CATEGORIES.PAYDAY] },
      { value: 'all', label: 'I want to check everything', categories: Object.values(AGENCY_CATEGORIES) },
    ],
  },
  {
    id: 'state',
    question: 'What state do you reside in?',
    type: 'select',
    options: [
      { value: 'FL', label: 'Florida', hasEnhancedRights: true, law: 'FDBR' },
      { value: 'CA', label: 'California', hasEnhancedRights: true, law: 'CCPA/CPRA' },
      { value: 'TX', label: 'Texas', hasEnhancedRights: true, law: 'TDPSA' },
      { value: 'CO', label: 'Colorado', hasEnhancedRights: true, law: 'CPA' },
      { value: 'CT', label: 'Connecticut', hasEnhancedRights: true, law: 'CTDPA' },
      { value: 'VA', label: 'Virginia', hasEnhancedRights: true, law: 'VCDPA' },
      { value: 'UT', label: 'Utah', hasEnhancedRights: true, law: 'UCPA' },
      { value: 'OR', label: 'Oregon', hasEnhancedRights: true, law: 'OCPA' },
      { value: 'NY', label: 'New York', hasEnhancedRights: true, law: 'SHIELD Act' },
      { value: 'OTHER', label: 'Other State', hasEnhancedRights: false },
    ],
  },
  {
    id: 'reports_filed',
    question: 'Have you completed the foundation steps?',
    type: 'checklist',
    options: [
      { value: 'ftc', label: 'Filed FTC report at IdentityTheft.gov' },
      { value: 'police', label: 'Filed police report with local precinct' },
      { value: 'irs', label: 'Secured IRS Identity Protection PIN (IP PIN)' },
      { value: 'credit_freeze', label: 'Froze credit at Big Three + Innovis + LexisNexis' },
      { value: 'dmv', label: 'Flagged driver license at DMV' },
    ],
  },
];

export const AGENCIES = [
  // PHASE 1: FOUNDATION
  {
    id: 'ftc_report',
    name: 'FTC Identity Theft Report',
    category: AGENCY_CATEGORIES.FEDERAL,
    phase: PHASES.FOUNDATION,
    priority: 1,
    description: 'File official federal identity theft report and create an official recovery affidavit',
    url: 'https://www.identitytheft.gov/',
    phone: '1-877-438-4338',
    actionType: 'File Report',
    estimatedTime: '30 minutes',
    required: true,
    instructions: [
      'Visit IdentityTheft.gov or call 1-877-ID-THEFT (1-877-438-4338) / TTY 1-866-653-4261',
      'Create an account or sign in to complete the official affidavit',
      'Select the types of fraud you are experiencing (tax, banking, credit, utilities)',
      'Download and print your completed FTC Identity Theft Report and Affidavit',
      'Save your report number - this legal document is required by police, credit bureaus, and creditors to discharge fraudulent debt',
    ],
  },
  {
    id: 'police_report',
    name: 'Local Police Report',
    category: AGENCY_CATEGORIES.LAW_ENFORCEMENT,
    phase: PHASES.FOUNDATION,
    priority: 2,
    description: 'File formal identity theft criminal report with local law enforcement',
    url: 'https://www.ic3.gov/',
    phone: 'Local Non-Emergency',
    actionType: 'File Report',
    estimatedTime: '1-2 hours',
    required: true,
    instructions: [
      'Bring your printed FTC Identity Theft Report, government photo ID, and evidence of fraud to your local police precinct',
      'Request a formal police report for identity theft (law enforcement is required to take a report under state identity theft statutes)',
      'Obtain the formal police report number and the investigating officer name and badge number',
      'Request multiple certified copies - many financial institutions and bureaus require an official copy',
      'If internet fraud, wire fraud, or cyber theft occurred, also file an online complaint at IC3.gov (FBI Internet Crime Complaint Center)',
    ],
  },
  {
    id: 'irs_pin',
    name: 'IRS Identity Protection PIN (IP PIN)',
    category: AGENCY_CATEGORIES.FEDERAL,
    phase: PHASES.FOUNDATION,
    priority: 3,
    description: 'Obtain a 6-digit PIN from the IRS to prevent fraudulent tax returns and stolen refunds',
    url: 'https://www.irs.gov/identity-theft-fraud-scams/get-an-identity-protection-pin',
    phone: '1-800-908-4490',
    actionType: 'Request IP PIN',
    estimatedTime: '15 minutes',
    required: true,
    critical: true,
    instructions: [
      'Visit irs.gov/get-an-ip-pin or call the IRS Identity Protection Specialized Unit at 1-800-908-4490',
      'Sign in through your IRS account / ID.me or verify identity via phone/mail',
      'Retrieve your unique 6-digit Identity Protection PIN (reissued annually each January)',
      'If a fraudulent tax return has already been filed using your SSN, complete and attach IRS Form 14039 (Identity Theft Affidavit)',
      'Once active, the IRS will reject any electronic or paper tax return submitted without your 6-digit PIN',
    ],
  },
  {
    id: 'ssa_account',
    name: 'Social Security Administration (SSA)',
    category: AGENCY_CATEGORIES.FEDERAL,
    phase: PHASES.FOUNDATION,
    priority: 4,
    description: 'Lock down your Social Security record, monitor earnings, and prevent benefit diversion',
    url: 'https://www.ssa.gov/myaccount/',
    phone: '1-800-772-1213',
    actionType: 'Secure Account & Review Earnings',
    estimatedTime: '20 minutes',
    required: true,
    instructions: [
      'Create or log into your "my Social Security" account at ssa.gov to prevent identity thieves from registering it in your name',
      'Review your Social Security Statement for unfamiliar employers or suspicious wage reports',
      'Call SSA Fraud Hotline at 1-800-269-0271 if unauthorized earnings or benefits are detected',
      'Request an electronic block on your SSN if your identity has been severely compromised',
    ],
  },
  {
    id: 'uspis_report',
    name: 'USPS Postal Inspection Service',
    category: AGENCY_CATEGORIES.FEDERAL,
    phase: PHASES.FOUNDATION,
    priority: 5,
    description: 'Check for fraudulent Change of Address (COA) filings and report diverted physical mail',
    url: 'https://www.uspis.gov/report',
    phone: '1-877-876-2455',
    actionType: 'Report Mail Diversion',
    estimatedTime: '15 minutes',
    required: false,
    instructions: [
      'Contact your local post office or call 1-877-876-2455 to verify whether a Change of Address (COA) has been submitted in your name',
      'If your regular mail has stopped arriving, immediately submit a mail theft report at uspis.gov/report',
      'Fraudulent address changes are the primary method criminals use to intercept replacement debit/credit cards and bank statements',
    ],
  },
  {
    id: 'annualcreditreport',
    name: 'AnnualCreditReport.com',
    category: AGENCY_CATEGORIES.CREDIT,
    phase: PHASES.FOUNDATION,
    priority: 6,
    description: 'Official site to get FREE weekly credit reports from all three major bureaus (Equifax, Experian, TransUnion)',
    url: 'https://www.annualcreditreport.com/',
    phone: '1-877-322-8228',
    actionType: 'Request Credit Reports',
    estimatedTime: '30 minutes',
    required: true,
    instructions: [
      'Visit AnnualCreditReport.com (the only federally authorized source for free weekly credit reports)',
      'Click "Request your free credit reports" button',
      'Fill out the request form and verify your identity with security questions',
      'Request and download all three credit reports: Equifax, Experian, and TransUnion',
      'Review each report carefully for unauthorized inquiries, fraudulent accounts, or wrong addresses',
      'Document all unauthorized lines of credit to submit dispute packets alongside your FTC and police reports',
    ],
  },
  {
    id: 'equifax_freeze',
    name: 'Equifax Credit Freeze',
    category: AGENCY_CATEGORIES.CREDIT,
    phase: PHASES.FOUNDATION,
    priority: 7,
    description: 'Place a free security freeze on your Equifax credit file to stop new account openings',
    url: 'https://www.equifax.com/personal/credit-report-services/credit-freeze/',
    phone: '1-800-349-9960 / 1-888-298-0045',
    actionType: 'Credit Freeze',
    estimatedTime: '10 minutes',
    required: true,
    critical: true,
    instructions: [
      'Visit the Equifax freeze center online or call automated line 1-800-349-9960 / 1-888-298-0045',
      'Verify your identity and request an immediate security freeze',
      'Securely record your freeze PIN or login credentials for future temporary lifts',
      'Under federal law (Economic Growth, Regulatory Relief, and Consumer Protection Act), freezes and unfreezes are 100% free',
    ],
  },
  {
    id: 'experian_freeze',
    name: 'Experian Credit Freeze',
    category: AGENCY_CATEGORIES.CREDIT,
    phase: PHASES.FOUNDATION,
    priority: 7,
    description: 'Place a free security freeze on your Experian credit file to stop new account openings',
    url: 'https://www.experian.com/freeze/center.html',
    phone: '1-888-397-3742',
    actionType: 'Credit Freeze',
    estimatedTime: '10 minutes',
    required: true,
    critical: true,
    instructions: [
      'Go to the Experian Freeze Center or call 1-888-397-3742',
      'Create an account or log in to place an immediate security freeze',
      'Ensure you choose "Security Freeze" (free by law) rather than paid credit lock subscriptions',
      'Store your unique PIN in a secure password manager',
    ],
  },
  {
    id: 'transunion_freeze',
    name: 'TransUnion Credit Freeze',
    category: AGENCY_CATEGORIES.CREDIT,
    phase: PHASES.FOUNDATION,
    priority: 7,
    description: 'Place a free security freeze on your TransUnion credit file to stop new account openings',
    url: 'https://www.transunion.com/credit-freeze',
    phone: '1-888-909-8872',
    actionType: 'Credit Freeze',
    estimatedTime: '10 minutes',
    required: true,
    critical: true,
    instructions: [
      'Visit the TransUnion Credit Freeze page or call 1-888-909-8872',
      'Complete the identity verification prompts and submit the freeze request',
      'Confirm freeze confirmation and save your PIN code',
      'Placing a freeze does not affect your credit score or prevent existing creditors from servicing accounts',
    ],
  },
  {
    id: 'innovis',
    name: 'Innovis (Fourth Bureau)',
    category: AGENCY_CATEGORIES.CREDIT,
    phase: PHASES.FOUNDATION,
    priority: 8,
    description: 'The fourth national credit reporting agency used by select major lenders and retailers',
    url: 'https://www.innovis.com/personal/securityFreeze',
    phone: '1-800-540-2505',
    actionType: 'Request Report & Freeze',
    estimatedTime: '15 minutes',
    required: true,
    instructions: [
      'Visit innovis.com/personal/securityFreeze or call 1-800-540-2505',
      'Request your free credit disclosure and place an immediate security freeze',
      'Verify your personal information and securely store your freeze PIN',
      'Often overlooked by consumers, Innovis is actively queried by retail credit card issuers and auto lenders',
    ],
  },
  {
    id: 'lexisnexis_freeze',
    name: 'LexisNexis Consumer Center & Freeze',
    category: AGENCY_CATEGORIES.CREDIT,
    phase: PHASES.FOUNDATION,
    priority: 9,
    description: 'Freeze the master public records and consumer reporting file used across insurance, banking, and real estate',
    url: 'https://consumer.risk.lexisnexis.com/freeze',
    phone: '1-866-863-2651',
    actionType: 'Request Report & Freeze',
    estimatedTime: '20 minutes',
    required: true,
    critical: true,
    instructions: [
      'Visit consumer.risk.lexisnexis.com/freeze or call 1-866-863-2651',
      'Place a security freeze on your LexisNexis Consumer File to prevent unauthorized lookups',
      'Request your Full File Disclosure to review property records, vehicle registrations, judgments, and address associations',
      'LexisNexis aggregates massive quantities of personal data and is queried by insurers, financial institutions, and background screeners',
    ],
  },
  {
    id: 'dmv',
    name: 'DMV Driver License Flag',
    category: AGENCY_CATEGORIES.DMV,
    phase: PHASES.FOUNDATION,
    priority: 10,
    description: 'Flag your driver license or state ID number to prevent imposter licenses and vehicle title fraud',
    actionType: 'Flag / Replace License',
    estimatedTime: '30 minutes',
    required: true,
    instructions: [
      'Contact your state DMV / driver license bureau',
      'Request a fraud alert flag on your driver license number',
      'If an imposter has obtained a duplicate or license in your name, present your FTC and police reports to request a new license number',
    ],
  },

  // PHASE 2: BANKING & CHECK VERIFICATION
  {
    id: 'chexsystems',
    name: 'ChexSystems',
    category: AGENCY_CATEGORIES.BANKING,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 11,
    description: 'Primary consumer database for checking and savings account history used by over 80% of US banks',
    url: 'https://www.chexsystems.com/security-freeze/place-freeze',
    phone: '1-800-428-9623',
    actionType: 'Request Report & Freeze',
    estimatedTime: '20 minutes',
    critical: true,
    instructions: [
      'Visit chexsystems.com or call 1-800-428-9623 to request your Consumer Disclosure Report',
      'Place a security freeze at chexsystems.com/security-freeze/place-freeze to block unauthorized bank account openings',
      'Review for unauthorized checking accounts, bounced checks, or unpaid overdraft fees reported in your name',
      'Submit dispute packet with FTC and police reports if fraudulent accounts are listed',
    ],
  },
  {
    id: 'ews',
    name: 'Early Warning Services (EWS)',
    category: AGENCY_CATEGORIES.BANKING,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 12,
    description: 'Bank fraud detection network that powers Zelle and real-time deposit monitoring for major institutions',
    url: 'https://www.earlywarning.com/consumer-information',
    phone: '1-855-372-8723',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    critical: true,
    instructions: [
      'Call EWS Consumer Services at 1-855-372-8723 or visit earlywarning.com/consumer-information',
      'Request your free annual EWS Consumer Report (includes deposit account openings and transaction flags)',
      'Review for fraudulent checking/savings accounts opened at major banks (JPMorgan Chase, BoA, Wells Fargo, Citi, etc.)',
      'Dispute any unauthorized bank accounts or fraudulent Zelle transfers',
    ],
  },
  {
    id: 'telecheck',
    name: 'TeleCheck (Fiserv)',
    category: AGENCY_CATEGORIES.BANKING,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 13,
    description: 'Check acceptance and verification network used by national retailers, grocers, and financial outlets',
    url: 'https://www.telecheck.com/consumers/',
    phone: '1-800-366-2425',
    actionType: 'Request Report & Dispute',
    estimatedTime: '15 minutes',
    instructions: [
      'Call TeleCheck Consumer Resolutions at 1-800-366-2425',
      'Request your TeleCheck file report and ask if your checking account is flagged for unpaid/declined checks',
      'If checks were forged in your name, request a check fraud dispute packet',
      'Provide your police report and closed account notification from your bank',
    ],
  },
  {
    id: 'certegy',
    name: 'Certegy Check Services',
    category: AGENCY_CATEGORIES.BANKING,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 14,
    description: 'Check risk management and check warranty service used by national retailers and supermarkets',
    url: 'https://www.certegy.com/inquiry-form/',
    phone: '1-800-237-8349',
    actionType: 'Request Report & Dispute',
    estimatedTime: '15 minutes',
    instructions: [
      'Call Certegy Consumer Support at 1-800-237-8349 or submit an inquiry at certegy.com/inquiry-form/',
      'Request your consumer check history report under the Fair Credit Reporting Act',
      'Check for declined or fraudulent checks written against your name or routing/account numbers',
      'Submit identity theft documentation to remove unauthorized check records',
    ],
  },
  {
    id: 'crosscheck',
    name: 'CrossCheck, Inc.',
    category: AGENCY_CATEGORIES.BANKING,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 15,
    description: 'Nationwide check guarantee agency utilized by retailers, automotive dealerships, and repair shops',
    url: 'https://www.cross-check.com/consumer-inquiry',
    phone: '1-800-654-2365',
    actionType: 'Request Report & Dispute',
    estimatedTime: '15 minutes',
    instructions: [
      'Contact CrossCheck Consumer Inquiries at 1-800-654-2365 or submit a request online',
      'Request your consumer file to verify whether fraudulent checks or declines are recorded',
      'Dispute unauthorized check transactions by submitting your police and FTC reports',
    ],
  },

  // PHASE 2: EMPLOYMENT SCREENING
  {
    id: 'work_number',
    name: 'The Work Number (Equifax)',
    category: AGENCY_CATEGORIES.EMPLOYMENT,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 16,
    description: 'Employment verification and salary history database; freeze prevents unauthorized access',
    url: 'https://employees.theworknumber.com/',
    phone: '1-800-996-7566',
    actionType: 'Freeze & Request Report',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit employees.theworknumber.com or call 1-800-996-7566',
      'Request your Employment Data Report (free annually)',
      'Place an Employment Data Freeze to stop third parties from querying your payroll and employment history',
      'Dispute fraudulent employers reporting wages under your SSN',
    ],
  },
  {
    id: 'first_advantage',
    name: 'First Advantage (FADV)',
    category: AGENCY_CATEGORIES.EMPLOYMENT,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 17,
    description: 'One of the largest global pre-employment screening providers used by Fortune 500 enterprises',
    url: 'https://fadv.com/consumer-center/',
    phone: '1-800-845-6004',
    actionType: 'Request File & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit fadv.com/consumer-center/ or call 1-800-845-6004',
      'Request your consumer background file under the FCRA',
      'Review for incorrect criminal records, mixed-identity files, or unauthorized employment histories',
      'Submit dispute documentation through the consumer dispute portal',
    ],
  },
  {
    id: 'hireright',
    name: 'HireRight',
    category: AGENCY_CATEGORIES.EMPLOYMENT,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 18,
    description: 'Major pre-employment background screening provider for technology, financial, and healthcare firms',
    url: 'https://www.hireright.com/for-candidates',
    phone: '1-866-521-6995',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit HireRight Candidate Portal at hireright.com/for-candidates or call 1-866-521-6995',
      'Select "Request Your File" to receive your full consumer background disclosure',
      'Review criminal records, drug screening records, and identity verification logs',
      'Dispute inaccurate or imposter employment records',
    ],
  },
  {
    id: 'sterling',
    name: 'Sterling',
    category: AGENCY_CATEGORIES.EMPLOYMENT,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 19,
    description: 'Global background screening agency used by corporate employers and government contractors',
    url: 'https://www.sterlingcheck.com/individuals/',
    phone: '1-800-853-5933',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit sterlingcheck.com/individuals/ or call 1-800-853-5933',
      'Request your background check file',
      'Verify that no fraudulent criminal charges or incorrect identity links exist',
      'File a formal dispute if errors are found',
    ],
  },
  {
    id: 'checkr',
    name: 'Checkr',
    category: AGENCY_CATEGORIES.EMPLOYMENT,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 20,
    description: 'Background check provider powering gig economy platforms (Uber, Lyft, DoorDash, Instacart)',
    url: 'https://candidate.checkr.com/',
    phone: '1-844-824-3257',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit candidate.checkr.com or call 1-844-824-3257',
      'Request your consumer file and review for identity mix-ups or imposter driving records',
      'Dispute fraudulent records directly in the candidate portal',
    ],
  },
  {
    id: 'accurate_background',
    name: 'Accurate Background',
    category: AGENCY_CATEGORIES.EMPLOYMENT,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 21,
    description: 'Enterprise pre-employment screening and criminal history verification agency',
    url: 'https://www.accurate.com/candidate-support/',
    phone: '1-800-216-8024',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit accurate.com/candidate-support/ or call 1-800-216-8024',
      'Request a copy of your background report disclosure',
      'Review for incorrect records or fraudulent employment entries',
      'Submit dispute documents directly to Candidate Support',
    ],
  },

  // PHASE 2: RENTAL & TENANT SCREENING
  {
    id: 'realpage',
    name: 'RealPage (LeasingDesk)',
    category: AGENCY_CATEGORIES.RENTAL,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 22,
    description: 'Tenant screening network used by major multi-family apartment communities nationwide',
    url: 'https://www.realpage.com/consumer-care/',
    phone: '1-866-934-5812',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Call RealPage Consumer Care at 1-866-934-5812 or visit realpage.com/consumer-care/',
      'Request your tenant screening report and LeasingDesk consumer file',
      'Review for unauthorized lease applications, evictions you didn\'t have, or rental debt',
      'Submit disputes in writing with your FTC and police reports attached',
    ],
  },
  {
    id: 'saferent',
    name: 'SafeRent Solutions (formerly CoreLogic SafeRent)',
    category: AGENCY_CATEGORIES.RENTAL,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 23,
    description: 'Specialized tenant screening database tracking eviction records and landlord-tenant civil court cases',
    url: 'https://saferentsolutions.com/consumer-support/',
    phone: '1-888-333-2413',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit saferentsolutions.com/consumer-support/ or call 1-888-333-2413',
      'Request your tenant screening report disclosure',
      'Check for fraudulent rental history or imposter court records',
      'Submit formal disputes with identity theft supporting documentation',
    ],
  },
  {
    id: 'appfolio',
    name: 'AppFolio Tenant Screening',
    category: AGENCY_CATEGORIES.RENTAL,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 24,
    description: 'Property management screening platform for residential properties and property management firms',
    url: 'https://www.appfolio.com/consumer',
    phone: '1-866-359-3630',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit appfolio.com/consumer or call 1-866-359-3630',
      'Request your consumer file to see rental screening checks performed on your profile',
      'Dispute unauthorized tenant inquiries or inaccurate lease breach notices',
    ],
  },
  {
    id: 'transunion_smartmove',
    name: 'TransUnion SmartMove',
    category: AGENCY_CATEGORIES.RENTAL,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 25,
    description: 'Tenant screening platform used by individual landlords and independent real estate owners',
    url: 'https://www.mysmartmove.com/',
    phone: '1-877-280-2844',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit mysmartmove.com or call 1-877-280-2844',
      'Request your SmartMove tenant screening history and check for unauthorized landlord inquiries',
      'Dispute fraudulent records via TransUnion\'s dispute process',
    ],
  },
  {
    id: 'experian_rentbureau',
    name: 'Experian RentBureau',
    category: AGENCY_CATEGORIES.RENTAL,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 26,
    description: 'Rental payment history database that reports lease payments into primary credit scoring models',
    url: 'https://www.experian.com/rentbureau/renter-faqs.html',
    phone: '1-877-704-2519',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Call Experian RentBureau at 1-877-704-2519',
      'Request your rental payment file and review for unfamiliar rental properties or delinquent lease marks',
      'Dispute fraudulent records through Experian\'s consumer dispute process',
    ],
  },
  {
    id: 'ntn',
    name: 'National Tenant Network (NTN)',
    category: AGENCY_CATEGORIES.RENTAL,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 27,
    description: 'Nationwide tenant background database tracking eviction filings, lease violations, and tenant scores',
    url: 'https://www.ntnonline.com/',
    phone: '1-800-228-0989',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit ntnonline.com or call 1-800-228-0989',
      'Request your tenant file report under the Fair Credit Reporting Act',
      'Review for unauthorized lease defaults or incorrect eviction filings',
      'Submit a dispute letter accompanied by identity theft affidavits',
    ],
  },

  // PHASE 2: TELECOM & UTILITIES
  {
    id: 'nctue',
    name: 'NCTUE (National Consumer Telecom & Utilities Exchange)',
    category: AGENCY_CATEGORIES.UTILITIES,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 28,
    description: 'Central exchange for cellular providers, electric, gas, water, and cable companies',
    url: 'https://www.nctue.com/',
    phone: '1-866-349-5355',
    actionType: 'Request Report & Freeze',
    estimatedTime: '20 minutes',
    critical: true,
    instructions: [
      'Visit nctue.com/Consumers or call 1-866-349-5355',
      'Request your NCTUE Full Disclosure Report (free annually)',
      'Place a Security Freeze on your NCTUE file to prevent criminals from opening cellular accounts or utility services in your name',
      'This database is completely separate from Equifax, Experian, and TransUnion',
    ],
  },

  // PHASE 2: RETAIL FRAUD TRACKING
  {
    id: 'retail_equation',
    name: 'The Retail Equation (TRE)',
    category: AGENCY_CATEGORIES.RETAIL,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 29,
    description: 'Return activity tracking system used by major retailers (Target, Best Buy, Home Depot, etc.)',
    url: 'https://www.theretailequation.com/Consumer/',
    phone: '1-800-652-2331',
    actionType: 'Request Report & Dispute',
    estimatedTime: '15 minutes',
    instructions: [
      'Visit theretailequation.com/Consumer/ or call 1-800-652-2331',
      'Request your Consumer Return Activity Report',
      'Check for fraudulent retail returns made with your stolen ID or driver license',
      'Submit dispute form with supporting identification if your return privileges have been restricted',
    ],
  },

  // PHASE 2: INSURANCE & MEDICAL
  {
    id: 'clue',
    name: 'LexisNexis C.L.U.E. (Auto & Property)',
    category: AGENCY_CATEGORIES.INSURANCE,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 30,
    description: 'Comprehensive Loss Underwriting Exchange tracking auto and homeowner insurance claims history',
    url: 'https://personalreports.lexisnexis.com/',
    phone: '1-866-312-8076',
    actionType: 'Request Report & Dispute',
    estimatedTime: '20 minutes',
    instructions: [
      'Visit personalreports.lexisnexis.com or call 1-866-312-8076',
      'Request your Auto C.L.U.E. and Property C.L.U.E. reports (both free annually)',
      'Review for unauthorized insurance claims, accidents, or vehicles/properties you never owned',
      'Submit disputes with your FTC and police reports to prevent inflated insurance premiums',
    ],
  },
  {
    id: 'mib',
    name: 'MIB Group (Medical Information Bureau)',
    category: AGENCY_CATEGORIES.INSURANCE,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 31,
    description: 'Underwriting database for individual life, health, and disability insurance; critical for medical identity theft',
    url: 'https://www.mib.com/request_your_record.html',
    phone: '1-866-692-6901',
    actionType: 'Request Consumer File',
    estimatedTime: '20 minutes',
    critical: true,
    instructions: [
      'Visit mib.com/request_your_record.html or call 1-866-692-6901',
      'Request your MIB Consumer File disclosure under the FCRA',
      'Review medical conditions and insurance applications submitted in your name',
      'Dispute unauthorized policies or medical conditions entered due to imposter medical care',
    ],
  },
  {
    id: 'verisk_aplus',
    name: 'A-PLUS by Verisk',
    category: AGENCY_CATEGORIES.INSURANCE,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 32,
    description: 'Auto and Property Loss Underwriting System used by insurers to assess property loss histories',
    url: 'https://www.verisk.com/insurance/products/a-plus/',
    phone: '1-800-627-0103',
    actionType: 'Request Loss History Report',
    estimatedTime: '20 minutes',
    instructions: [
      'Call Verisk Consumer Inquiries at 1-800-627-0103',
      'Request your A-PLUS loss history disclosure',
      'Verify that no fraudulent property or vehicle loss claims are tied to your profile',
    ],
  },

  // PHASE 2: PAYDAY & SUBPRIME LENDING
  {
    id: 'clarity',
    name: 'Clarity Services (Experian Subprime)',
    category: AGENCY_CATEGORIES.PAYDAY,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 33,
    description: 'Alternative credit bureau tracking payday loans, rent-to-own, and subprime lending',
    url: 'https://www.clarityservices.com/consumers/',
    phone: '1-866-390-3118',
    actionType: 'Request Report & Dispute',
    estimatedTime: '15 minutes',
    instructions: [
      'Call Clarity Services at 1-866-390-3118 or visit clarityservices.com/consumers/',
      'Request your Clarity consumer disclosure report',
      'Review for unauthorized payday loans, title loans, or installment debts',
      'Dispute fraudulent inquiries and accounts with identity theft documentation',
    ],
  },
  {
    id: 'datax',
    name: 'DataX (Equifax Subprime)',
    category: AGENCY_CATEGORIES.PAYDAY,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 34,
    description: 'Specialty consumer reporting agency tracking short-term lending, payday loans, and check cashing',
    url: 'https://www.datax-ltd.com/consumers/',
    phone: '1-800-218-7868',
    actionType: 'Request Report & Dispute',
    estimatedTime: '15 minutes',
    instructions: [
      'Call DataX at 1-800-218-7868',
      'Request your consumer disclosure report',
      'Review for high-risk loan inquiries or storefront payday loans opened with your credentials',
      'File written disputes with copies of your FTC report',
    ],
  },
  {
    id: 'factortrust',
    name: 'FactorTrust (TransUnion Subprime)',
    category: AGENCY_CATEGORIES.PAYDAY,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 35,
    description: 'Alternative credit bureau specializing in short-term installment loans and online fintech lending',
    url: 'https://www.factortrust.com/consumers/',
    phone: '1-877-634-0806',
    actionType: 'Request Report & Dispute',
    estimatedTime: '15 minutes',
    instructions: [
      'Call FactorTrust at 1-877-634-0806',
      'Request your consumer disclosure report',
      'Review for online line-of-credit or installment loan accounts',
      'Submit dispute packet for unauthorized accounts',
    ],
  },
  {
    id: 'teletrack',
    name: 'Equifax Teletrack',
    category: AGENCY_CATEGORIES.PAYDAY,
    phase: PHASES.HIDDEN_BUREAUS,
    priority: 36,
    description: 'Specialized consumer reporting agency tracking subprime loans, rent-to-own leases, and furniture rentals',
    url: 'https://teletrack.com',
    phone: '1-877-309-5226',
    actionType: 'Request Report & Freeze',
    estimatedTime: '15 minutes',
    instructions: [
      'Call Teletrack at 1-877-309-5226 or visit teletrack.com',
      'Request your consumer disclosure report and place a security freeze',
      'Check for lease-to-own agreements or subprime credit inquiries',
    ],
  },
];

export const STATE_SPECIFIC_INFO = {
  FL: {
    name: 'Florida',
    law: 'Florida Digital Bill of Rights (FDBR)',
    description: 'Comprehensive protections for personal data and victim support',
    rights: [
      'Right to confirm whether a controller processes your personal data and access it',
      'Right to request deletion or correction of inaccurate personal data',
      'Right to opt-out of the sale of personal data, targeted advertising, and sensitive profiling',
      'Statutory right to a free police report for identity theft under Florida Statute § 817.568',
      'Right to have fraudulent records expedited for investigation by financial institutions',
    ],
    resources: [
      {
        name: 'Florida Attorney General - Identity Theft Resources',
        url: 'https://www.myfloridalegal.com/consumer-protection',
      },
    ],
  },
  CA: {
    name: 'California',
    law: 'CCPA/CPRA',
    description: 'Strongest consumer privacy rights and data broker registry in the US',
    rights: [
      'Right to request deletion of personal data from data brokers and controllers',
      'Right to opt-out of sale or sharing of personal information',
      'Right to correct inaccurate personal data',
      'Right to limit the use and disclosure of sensitive personal information',
      'Private right of action for data breach victims under California Civil Code § 1798.150',
    ],
    resources: [
      {
        name: 'California Attorney General - Privacy Rights',
        url: 'https://oag.ca.gov/privacy/ccpa',
      },
      {
        name: 'California Privacy Protection Agency (CPPA)',
        url: 'https://cppa.ca.gov/',
      },
    ],
  },
  TX: {
    name: 'Texas',
    law: 'Texas Data Privacy and Security Act (TDPSA)',
    description: 'Comprehensive consumer data rights and data broker transparency',
    rights: [
      'Right to access, correct, and delete personal data held by controllers',
      'Right to opt out of the processing of personal data for targeted advertising or sales',
      'Right to obtain a copy of your personal data in a portable format',
    ],
    resources: [
      {
        name: 'Texas Attorney General - Consumer Protection',
        url: 'https://www.texasattorneygeneral.gov/consumer-protection',
      },
    ],
  },
  CO: {
    name: 'Colorado',
    law: 'Colorado Privacy Act (CPA)',
    description: 'Comprehensive privacy law with universal opt-out rights',
    rights: [
      'Right to opt-out of data sales and targeted profiling',
      'Right to correct inaccurate data',
      'Right to delete personal data held by controllers',
    ],
  },
  CT: {
    name: 'Connecticut',
    law: 'Connecticut Data Privacy Act (CTDPA)',
    description: 'Robust privacy protections and consumer control',
    rights: [
      'Right to access, correct, and delete personal data',
      'Right to opt-out of data sales and profiling',
    ],
  },
  VA: {
    name: 'Virginia',
    law: 'Virginia Consumer Data Protection Act (VCDPA)',
    description: 'Consumer data protection rights',
    rights: [
      'Right to opt-out of targeted advertising and data sales',
      'Right to access, correct, and delete personal information',
    ],
  },
  UT: {
    name: 'Utah',
    law: 'Utah Consumer Privacy Act (UCPA)',
    description: 'Consumer privacy protections',
    rights: [
      'Right to access and delete personal data',
      'Right to opt-out of sale of personal data and targeted advertising',
    ],
  },
  OR: {
    name: 'Oregon',
    law: 'Oregon Consumer Privacy Act (OCPA)',
    description: 'Enhanced protections including specific third-party disclosure rights',
    rights: [
      'Right to obtain a list of specific third parties to which personal data was disclosed',
      'Right to correct inaccuracies and delete personal data',
      'Right to opt-out of targeted advertising and profiling',
    ],
  },
  NY: {
    name: 'New York',
    law: 'SHIELD Act',
    description: 'Specific protections and breach notification mandates for victims',
    rights: [
      'Mandatory free credit monitoring provided by breach entities',
      'Enhanced notification requirements and security safeguard standards',
    ],
  },
};
